import { useMemo } from 'react';
import { CHECKBOX_STATUS } from 'ui/checkbox/checkboxStatus';
import { HeaderGridRow } from 'ui/indexPages/tables/grid';
import { isSelectionContent, isSelectionFolder } from 'utils/contexts/selection';
import { useSortingContext } from 'utils/contexts/sorting';

import { StyledGroupSelect } from '../CampaignIndexTable.styles';
import { TableHeaderButton } from '../components/TableHeaderButton';
import { CAMPAIGN_HEADER_GRIDS, CAMPAIGN_INDEX_COLUMNS, DELIVERY, ENGAGEMENT } from '../constants';
import { Column } from '../types';
import { useCampaignQueryContext } from './contexts/CampaignQueryContext';
import { useSelectionContext } from './contexts/SelectionContext';

type CampaignTableHeaderProps = {
  columns?: Column[];
};

export const CampaignTableHeader = ({
  columns = CAMPAIGN_INDEX_COLUMNS,
}: CampaignTableHeaderProps) => {
  const { sortBy, sortDirection, setSortBy, setSortDirection } = useSortingContext();
  const { addPageToBulk, removePageFromBulk, bulk } = useSelectionContext();
  const { campaigns, folders } = useCampaignQueryContext();

  const totalSelectedItemsOnPage = useMemo(() => {
    const selectedFolderIds = bulk.filter(isSelectionFolder).map((item) => item.id);
    const selectedCampaignIds = bulk.filter(isSelectionContent).map((item) => item.id);
    const foldersSelectedOnPage = folders.filter(({ id }) => selectedFolderIds.includes(id));
    const campaignsSelectedOnPage = campaigns.filter(({ id }) => selectedCampaignIds.includes(id));
    return foldersSelectedOnPage.length + campaignsSelectedOnPage.length;
  }, [folders, campaigns, bulk]);

  const totalSelectableItems = folders.length + campaigns.length;

  let groupSelectStatus = CHECKBOX_STATUS.INDETERMINATE;
  if (bulk.length === 0) {
    groupSelectStatus = CHECKBOX_STATUS.UNCHECKED;
  } else if (totalSelectedItemsOnPage === totalSelectableItems) {
    groupSelectStatus = CHECKBOX_STATUS.CHECKED;
  }

  return (
    <HeaderGridRow gridLayout={CAMPAIGN_HEADER_GRIDS}>
      <StyledGroupSelect
        onSelectAll={addPageToBulk}
        onSelectNone={removePageFromBulk}
        status={groupSelectStatus}
      />
      {columns.map((column) => (
        <TableHeaderButton
          key={column.id}
          sortDirection={sortDirection}
          onSortByChange={setSortBy}
          onSortDirectionChange={setSortDirection}
          isActiveSortColumn={column.id === sortBy}
          isSortable={column.id !== DELIVERY && column.id !== ENGAGEMENT}
          {...column}
        />
      ))}
    </HeaderGridRow>
  );
};
